import React from "react";
import {
  Box,
  Typography,
  Modal,
  IconButton,
} from "@mui/material";
import designTokens from "../../styles/designTokens";
import type { UploadFlow } from "../../types";

interface StopUploadModalProps {
  open: boolean;
  onContinue: () => void;
  onStop: () => void;
  fileName?: string | null;
  flow?: UploadFlow;
  currentStep?: "uploading" | "converting" | "optimizing";
}

const DANGER_RED = "#FF4242";
const STOP_PROCESSING_MODAL = designTokens.semantic.stopProcessingModal;
const STEP_ITEM_WIDTH = STOP_PROCESSING_MODAL.stepItemWidth;
const STEP_ITEM_GAP = STOP_PROCESSING_MODAL.stepGap;
const STEP_ICON_SIZE = STOP_PROCESSING_MODAL.stepIconSize;
const STEP_CONNECTOR_HEIGHT = 2;
const STEP_CONNECTOR_TOP = STEP_ICON_SIZE / 2 - STEP_CONNECTOR_HEIGHT / 2;
const STEP_CONNECTOR_LEFT = STEP_ITEM_WIDTH / 2;
const STEP_CONNECTOR_WIDTH = STEP_ITEM_WIDTH;
const STEP_TRACKER_WIDTH = STEP_ITEM_WIDTH * 3 + STEP_ITEM_GAP * 2;
const LIBRARY_BG = "#1E1E20";
const LIBRARY_TEXT_MUTED = "#78787C";
const LIBRARY_STEP_ACTIVE = "#50B786";
const LIBRARY_STEP_LABEL_WIDTHS = [89.063, 117.086, 114.734] as const;

interface StepConfig {
  label: string;
  state: "completed" | "in_progress" | "waiting";
  helper: string;
}

const getSteps = (
  currentStep: NonNullable<StopUploadModalProps["currentStep"]>,
): StepConfig[] => {
  const steps: StepConfig[] = [
    { label: "Uploading object", state: "waiting", helper: "Waiting" },
    { label: "Converting format", state: "waiting", helper: "Waiting" },
    { label: "Removing original", state: "waiting", helper: "Waiting" },
  ];

  if (currentStep === "uploading") {
    steps[0].state = "in_progress";
    steps[0].helper = "In progress...";
  } else if (currentStep === "converting") {
    steps[0].state = "completed";
    steps[0].helper = "Completed";
    steps[1].state = "in_progress";
    steps[1].helper = "In progress...";
  } else {
    steps[0].state = "completed";
    steps[0].helper = "Completed";
    steps[1].state = "completed";
    steps[1].helper = "Completed";
    steps[2].state = "in_progress";
    steps[2].helper = "In progress...";
  }

  return steps;
};

const StepCheckIcon: React.FC = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    aria-hidden="true"
    sx={{ width: 24, height: 24, display: "block" }}
  >
    <path
      d="M7.2 12.45L10.35 15.6L16.8 9.15"
      fill="none"
      stroke="#FFFFFF"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

const StepProgressIcon: React.FC = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    aria-hidden="true"
    sx={{
      width: 26.32,
      height: 26.32,
      display: "block",
      transform: "rotate(105deg)",
    }}
  >
    <path
      d="M18.3 8.65A7.2 7.2 0 1 0 18.95 12"
      fill="none"
      stroke="#FFFFFF"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M18.1 6.05V10.2H13.95"
      fill="none"
      stroke="#FFFFFF"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

const StepTrashIcon: React.FC = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    aria-hidden="true"
    sx={{ width: 24, height: 24, display: "block" }}
  >
    <path
      d="M9 9V16.9"
      fill="none"
      stroke={LIBRARY_TEXT_MUTED}
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <path
      d="M12 9V16.9"
      fill="none"
      stroke={LIBRARY_TEXT_MUTED}
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <path
      d="M15 9V16.9"
      fill="none"
      stroke={LIBRARY_TEXT_MUTED}
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <path
      d="M7 7H17"
      fill="none"
      stroke={LIBRARY_TEXT_MUTED}
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <path
      d="M9.75 7V5.95C9.75 5.4 10.2 4.95 10.75 4.95H13.25C13.8 4.95 14.25 5.4 14.25 5.95V7"
      fill="none"
      stroke={LIBRARY_TEXT_MUTED}
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 7V17.2C8 17.86 8.54 18.4 9.2 18.4H14.8C15.46 18.4 16 17.86 16 17.2V7"
      fill="none"
      stroke={LIBRARY_TEXT_MUTED}
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

const CloseGlyph: React.FC = () => (
  <Box
    component="svg"
    viewBox="0 0 12 12"
    aria-hidden="true"
    sx={{ width: 12, height: 12, display: "block" }}
  >
    <path
      d="M2 2L10 10"
      fill="none"
      stroke={LIBRARY_TEXT_MUTED}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M10 2L2 10"
      fill="none"
      stroke={LIBRARY_TEXT_MUTED}
      strokeWidth="2"
      strokeLinecap="round"
    />
  </Box>
);

const modalButtonBaseSx = {
  height: 48,
  minWidth: 100,
  pl: designTokens.numbers.gap[8],
  pr: designTokens.numbers.gap[12],
  py: "10px",
  borderRadius: designTokens.numbers.cornerRadius[8],
  cursor: "pointer",
  boxSizing: "border-box" as const,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  whiteSpace: "nowrap" as const,
  ...designTokens.typography.styles.body,
};

const libraryButtonBaseSx = {
  ...modalButtonBaseSx,
  fontSize: "16px",
  lineHeight: "20px",
  fontWeight: 400,
  letterSpacing: 0,
  flexShrink: 0,
  px: 0,
};

const StepIcon: React.FC<{ state: StepConfig["state"] }> = ({ state }) => {
  if (state === "completed") {
    return (
      <Box
        sx={{
          width: STEP_ICON_SIZE,
          height: STEP_ICON_SIZE,
          borderRadius: "999px",
          bgcolor: LIBRARY_STEP_ACTIVE,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          zIndex: 1,
          flexShrink: 0,
        }}
      >
        <StepCheckIcon />
      </Box>
    );
  }

  if (state === "in_progress") {
    return (
      <Box
        sx={{
          width: STEP_ICON_SIZE,
          height: STEP_ICON_SIZE,
          borderRadius: "999px",
          bgcolor: LIBRARY_STEP_ACTIVE,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          zIndex: 1,
          flexShrink: 0,
        }}
      >
        <StepProgressIcon />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: STEP_ICON_SIZE,
        height: STEP_ICON_SIZE,
        borderRadius: "999px",
        bgcolor: LIBRARY_BG,
        border: `2px solid ${LIBRARY_TEXT_MUTED}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        zIndex: 1,
        flexShrink: 0,
      }}
    >
      <StepTrashIcon />
    </Box>
  );
};

const stepperFlows: UploadFlow[] = [
  "home_library_import",
  "editor_object_import",
  "editor_library_attach",
];

export const truncateStopProcessingFileName = (
  name: string,
  maxChars = STOP_PROCESSING_MODAL.fileNameMaxChars,
): string => {
  if (name.length <= maxChars) {
    return name;
  }

  return `${name.slice(0, maxChars).trimEnd()}...`;
};

const StopUploadModal: React.FC<StopUploadModalProps> = ({
  open,
  onContinue,
  onStop,
  fileName,
  flow,
  currentStep = "uploading",
}) => {
  const showLibraryStepper = flow != null && stepperFlows.includes(flow);
  const steps = getSteps(currentStep);
  const displayFileName = fileName
    ? truncateStopProcessingFileName(fileName)
    : null;

  if (!showLibraryStepper) {
    return (
      <Modal
        open={open}
        onClose={onContinue}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        }}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box
          sx={{
            width: designTokens.semantic.modalWidth,
            minWidth: designTokens.semantic.modalWidth,
            backgroundColor: designTokens.fill.background.grey20.dark,
            borderRadius: designTokens.numbers.cornerRadius[16],
            outline: "none",
            overflow: "hidden",
            boxShadow: designTokens.shadows.modal,
            p: designTokens.numbers.gap[24],
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: designTokens.numbers.gap[8],
            }}
          >
            <Typography
              sx={{
                ...designTokens.typography.styles.heading,
                color: designTokens.fill.text.grey90.dark,
              }}
            >
              Stop processing?
            </Typography>
            <IconButton
              aria-label="close"
              onClick={onContinue}
              sx={{
                p: 0,
                width: 24,
                height: 24,
                color: designTokens.fill.icon.grey60.dark,
                "&:hover": { backgroundColor: "transparent" },
              }}
            >
              <CloseGlyph />
            </IconButton>
          </Box>

          <Typography
            sx={{
              ...designTokens.typography.styles.headline1,
              color: designTokens.fill.text.grey90.dark,
              mb: designTokens.numbers.gap[32],
              whiteSpace: "pre-line",
            }}
          >
            {displayFileName
              ? `The file "${fileName}" is still being processed.\nAre you sure you want to stop?`
              : "The upload is not complete yet. Are you sure you want to stop?"}
          </Typography>

          <Box sx={{ display: "flex", gap: designTokens.numbers.gap[12], justifyContent: "flex-end" }}>
            <Box
              component="button"
              type="button"
              onClick={onContinue}
              sx={{
                backgroundColor: "transparent",
                border: `1px solid ${designTokens.stroke.grey60.dark}`,
                color: designTokens.fill.text.grey80.dark,
                ...modalButtonBaseSx,
              }}
            >
              Continue processing
            </Box>
            <Box
              component="button"
              type="button"
              onClick={onStop}
              sx={{
                backgroundColor: designTokens.semantic.deleteButtonOutline.bg,
                border: `1px solid ${DANGER_RED}`,
                color: DANGER_RED,
                ...modalButtonBaseSx,
              }}
            >
              Stop processing
            </Box>
          </Box>
        </Box>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onContinue}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
        },
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: designTokens.numbers.gap[32],
      }}
    >
      <Box
        sx={{
          width: STOP_PROCESSING_MODAL.width,
          minWidth: STOP_PROCESSING_MODAL.width,
          maxWidth: STOP_PROCESSING_MODAL.width,
          height: STOP_PROCESSING_MODAL.height,
          backgroundColor: designTokens.fill.background.grey20.dark,
          borderRadius: designTokens.numbers.cornerRadius[16],
          outline: "none",
          overflow: "hidden",
          boxShadow: designTokens.shadows.modal,
          display: "flex",
          flexDirection: "column",
        }}
        data-testid="stop-upload-library-modal"
      >
        <Box
          sx={{
            height: STOP_PROCESSING_MODAL.barHeight,
            width: "100%",
            bgcolor: designTokens.fill.background.grey20.dark,
            flexShrink: 0,
          }}
        />
        <Box
          sx={{
            height: STOP_PROCESSING_MODAL.headerHeight,
            px: `${STOP_PROCESSING_MODAL.horizontalPadding}px`,
            display: "flex",
            alignItems: "center",
            gap: designTokens.numbers.gap[8],
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              ...designTokens.typography.styles.viewrStudio.headingMedium,
              color: designTokens.fill.text.grey90.dark,
              flex: 1,
            }}
          >
            Stop processing?
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onContinue}
            sx={{
              p: 0,
              width: 24,
              height: 24,
              color: designTokens.fill.icon.grey60.dark,
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            <CloseGlyph />
          </IconButton>
        </Box>

        <Box
          sx={{
            height: STOP_PROCESSING_MODAL.descriptionHeight,
            px: `${STOP_PROCESSING_MODAL.horizontalPadding}px`,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Typography
            data-testid="stop-upload-description-line-1"
            sx={{
              ...designTokens.typography.styles.viewrStudio.headline1Regular,
              color: designTokens.fill.text.grey90.dark,
              width: "100%",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {displayFileName
              ? `The file "${displayFileName}" is still being processed.`
              : "The file is still being processed."}
          </Typography>
          <Typography
            data-testid="stop-upload-description-line-2"
            sx={{
              ...designTokens.typography.styles.viewrStudio.headline1Regular,
              color: designTokens.fill.text.grey90.dark,
              width: "100%",
            }}
          >
            Are you sure you want to stop?
          </Typography>
        </Box>

        <Box
          sx={{
            height: STOP_PROCESSING_MODAL.processHeight,
            display: "flex",
            justifyContent: "center",
            pt: `${STOP_PROCESSING_MODAL.processTopPadding}px`,
            pb: `${STOP_PROCESSING_MODAL.processBottomPadding}px`,
            px: `${STOP_PROCESSING_MODAL.processHorizontalPadding}px`,
            boxSizing: "border-box",
            flexShrink: 0,
          }}
          data-testid="stop-upload-stepper"
        >
          <Box
            sx={{
              width: STEP_TRACKER_WIDTH,
              maxWidth: "100%",
              display: "flex",
              gap: `${STEP_ITEM_GAP}px`,
              alignItems: "flex-start",
              justifyContent: "flex-start",
            }}
          >
            {steps.map((step, idx) => (
              <React.Fragment key={step.label}>
                <Box
                  sx={{ width: STEP_ITEM_WIDTH, height: 92, position: "relative", flexShrink: 0 }}
                >
                  {idx < steps.length - 1 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: STEP_CONNECTOR_TOP,
                        left: STEP_CONNECTOR_LEFT,
                        width: STEP_CONNECTOR_WIDTH,
                        height: STEP_CONNECTOR_HEIGHT,
                        bgcolor:
                          step.state === "completed"
                            ? LIBRARY_STEP_ACTIVE
                            : LIBRARY_TEXT_MUTED,
                        zIndex: 0,
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: designTokens.numbers.gap[12],
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <StepIcon state={step.state} />
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: designTokens.numbers.gap[4],
                        width: LIBRARY_STEP_LABEL_WIDTHS[idx],
                      }}
                    >
                    <Typography
                      sx={{
                        ...designTokens.typography.styles.viewrStudio.label1Medium,
                        color:
                          step.state === "waiting"
                            ? designTokens.fill.text.grey60.dark
                            : step.state === "in_progress"
                              ? designTokens.fill.text.common100.dark
                              : designTokens.fill.text.grey80.dark,
                        textAlign: "center",
                        whiteSpace: "nowrap",
                      }}
                      >
                        {step.label}
                      </Typography>
                    <Typography
                      sx={{
                        ...designTokens.typography.styles.viewrStudio.captionRegular,
                        color:
                          step.state === "waiting"
                            ? designTokens.fill.text.grey60.dark
                            : designTokens.fill.text.primary50.dark,
                        textAlign: "center",
                        whiteSpace: "nowrap",
                      }}
                      >
                        {step.helper}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </React.Fragment>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            height: STOP_PROCESSING_MODAL.buttonRowHeight,
            px: `${STOP_PROCESSING_MODAL.horizontalPadding}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: `${STOP_PROCESSING_MODAL.buttonGap}px`,
            flexShrink: 0,
          }}
          data-testid="stop-upload-actions"
        >
          <Box
            component="button"
            type="button"
            onClick={onContinue}
            sx={{
              width: STOP_PROCESSING_MODAL.continueButtonWidth,
              backgroundColor: "transparent",
              border: `1px solid ${designTokens.stroke.grey60.dark}`,
              color: designTokens.fill.text.grey80.dark,
              ...libraryButtonBaseSx,
            }}
          >
            <Box
              component="span"
              sx={{
                width: "100%",
                height: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Continue processing
            </Box>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={onStop}
            sx={{
              width: STOP_PROCESSING_MODAL.stopButtonWidth,
              backgroundColor: designTokens.semantic.deleteButtonOutline.bg,
              border: `1px solid ${designTokens.semantic.deleteButtonOutline.border}`,
              color: designTokens.semantic.deleteButtonOutline.text,
              ...libraryButtonBaseSx,
            }}
          >
            <Box
              component="span"
              sx={{
                width: "100%",
                height: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Stop processing
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            height: STOP_PROCESSING_MODAL.barHeight,
            width: "100%",
            bgcolor: designTokens.fill.background.grey20.dark,
            flexShrink: 0,
          }}
        />
      </Box>
    </Modal>
  );
};

export default StopUploadModal;
